import { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Database, PlugZap, RotateCcw, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function DBConfig() {
  const [current, setCurrent] = useState(null);
  const [mongoUrl, setMongoUrl] = useState("");
  const [dbName, setDbName] = useState("");
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = () => api.get("/admin/db-config").then((r) => setCurrent(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const test = async () => {
    if (!mongoUrl || !dbName) { toast.error("Fill both fields"); return; }
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/admin/db-config/test", { mongo_url: mongoUrl, db_name: dbName });
      setTestResult(data);
      if (data.ok) toast.success(`Connected · ${data.collections} collections found`);
      else toast.error(data.error || "Connection failed");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setTesting(false); }
  };

  const save = async () => {
    if (!mongoUrl || !dbName) { toast.error("Fill both fields"); return; }
    if (!window.confirm("Switch the live database? All subsequent reads/writes will use the new connection.")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/db-config", { mongo_url: mongoUrl, db_name: dbName });
      toast.success(`Switched to ${data.active_db_name}`);
      load();
      setMongoUrl(""); setDbName(""); setTestResult(null);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset to .env default MongoDB? The custom override will be removed.")) return;
    try {
      await api.post("/admin/db-config/reset");
      toast.success("Reset to default");
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-db-config-page">
      <div>
        <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-600 mb-2">05 / Infrastructure</div>
        <h1 className="rule-text text-3xl sm:text-4xl text-zinc-900 dark:text-zinc-100">Database.</h1>
        <p className="text-sm text-white0 mt-2 max-w-2xl">Configure the MongoDB connection used by the application. All data will be read and written to the selected database.</p>
      </div>

      {/* Current status */}
      <div className="bg-zinc-900 text-white p-6 flex items-center justify-between" data-testid="current-db-status">
        <div className="flex items-center gap-4">
          <div className="p-3 border border-stone-700 bg-zinc-50"><Database size={20} /></div>
          <div>
            <div className="eyebrow text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Active Connection</div>
            <div className="rule-text text-2xl mt-1 font-mono break-all">{current?.active_mongo_url || "—"}</div>
            <div className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Database: <span className="font-bold text-white">{current?.active_db_name || "—"}</span> · {current?.using_custom ? "Custom override" : "Using .env default"}</div>
          </div>
        </div>
        {current?.using_custom && (
          <Button onClick={reset} variant="outline" className="rounded-xl bg-transparent border-stone-700 text-stone-200 hover:bg-zinc-50 uppercase tracking-[0.2em] text-xs font-bold h-10" data-testid="reset-db-btn">
            <RotateCcw size={14} className="mr-2" /> Reset to Default
          </Button>
        )}
      </div>

      {/* Connect form */}
      <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm p-6 space-y-4">
        <div>
          <div className="eyebrow">Connect to MongoDB</div>
          <div className="text-xs text-white0 mt-1">Paste your MongoDB URI (local, Atlas, or any remote host) and the database name.</div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="eyebrow">Mongo URI</Label>
            <Input
              value={mongoUrl}
              onChange={(e) => setMongoUrl(e.target.value)}
              placeholder="mongodb+srv://user:password@cluster0.xxxxx.mongodb.net/ or mongodb://host:27017"
              className="rounded-xl mt-1 font-mono text-xs h-11"
              data-testid="mongo-url-input"
            />
            <div className="text-[11px] text-white0 mt-1">Supports both <code>mongodb://</code> and <code>mongodb+srv://</code> schemes.</div>
          </div>
          <div>
            <Label className="eyebrow">Database Name</Label>
            <Input
              value={dbName}
              onChange={(e) => setDbName(e.target.value)}
              placeholder="ems_database"
              className="rounded-xl mt-1 font-mono h-11"
              data-testid="db-name-input"
            />
          </div>
        </div>

        {testResult && (
          <div className={`border p-4 flex items-start gap-3 ${testResult.ok ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-red-50 border-red-300 text-red-700"}`} data-testid="test-result">
            {testResult.ok ? <CheckCircle2 size={18} className="mt-0.5" /> : <XCircle size={18} className="mt-0.5" />}
            <div className="text-sm">
              <div className="eyebrow">{testResult.ok ? "Connection successful" : "Connection failed"}</div>
              {testResult.ok ? <div className="mt-1">Found {testResult.collections} collection(s) in database.</div> : <div className="mt-1 font-mono text-xs">{testResult.error}</div>}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button onClick={test} disabled={testing} variant="outline" className="rounded-xl border-zinc-300 uppercase tracking-[0.2em] text-xs font-bold h-11" data-testid="test-connection-btn">
            <PlugZap size={14} className="mr-2" /> {testing ? "Testing..." : "Test Connection"}
          </Button>
          <Button onClick={save} disabled={saving || !testResult?.ok} className="klein-btn rounded-xl uppercase tracking-[0.2em] text-xs font-bold h-11" data-testid="save-db-btn">
            {saving ? "Switching..." : "Save & Switch"}
          </Button>
        </div>
        {!testResult?.ok && <p className="text-[11px] text-white0">Run a successful test before saving — this prevents breaking the live app with a bad connection.</p>}
      </div>

      {/* Warnings */}
      <div className="bg-amber-50 border border-amber-300 p-5 text-sm text-amber-900 space-y-2" data-testid="db-warnings">
        <div className="eyebrow text-amber-900">Important Notes</div>
        <ul className="list-disc pl-5 space-y-1 text-xs">
          <li>Switching databases reconnects the live app immediately. Users already signed in may see stale data until next request.</li>
          <li>On switch, required indexes and a default admin user (<span className="font-mono">{current?.default_db_name ? "admin@example.com" : "—"}</span>) are created if missing.</li>
          <li>File uploads are stored in Emergent Object Storage — not in MongoDB — and continue to work across DB switches.</li>
          <li>To revert, click <span className="font-bold">Reset to Default</span> to fall back to the environment-configured DB.</li>
        </ul>
      </div>
    </div>
  );
}
